/**
 * DOM overlays: loading screen (04: bundle progress + the race line), help overlay (`?`), debug overlay (`` ` ``, 05 Tooling), landscape gate (overhaul plan §3.6).
 */
import type { LoadProgress } from '../core/api.ts';
import type { Provenance } from '../core/provenance.ts';
import { TI_POSITIONS_LABEL, positionsHelpKind } from '../core/provenance.ts';
import { VERSION_LABEL } from '../version.ts';
import type { LiteSetting } from '../core/layout/lite.ts';
import { KEY_HELP } from './keyboard.ts';

export class LoadingScreen {
  readonly el: HTMLDivElement;
  private readonly bar: HTMLDivElement;
  private readonly phase: HTMLDivElement;
  private readonly sub: HTMLDivElement;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.id = 'loading';
    this.el.innerHTML = `
      <div class="card">
        <div class="title">UNDERCUT</div>
        <div class="sub" hidden></div>
        <div class="progress"><div class="fill"></div></div>
        <div class="phase">LOADING</div>
        <div class="version">UNDERCUT ${esc(VERSION_LABEL)}</div>
      </div>`;
    parent.appendChild(this.el);
    this.bar = this.el.querySelector('.fill') as HTMLDivElement;
    this.phase = this.el.querySelector('.phase') as HTMLDivElement;
    this.sub = this.el.querySelector('.sub') as HTMLDivElement;
  }

  /** The race line ('ITALIAN GRAND PRIX 2024 · MONZA'), once the catalog/manifest race is known. */
  setRaceLine(line: string): void {
    this.sub.textContent = line;
    this.sub.hidden = false;
  }

  progress(p: LoadProgress): void {
    const f = p.total > 0 ? Math.min(1, p.loaded / p.total) : 0;
    this.bar.style.width = `${(f * 100).toFixed(1)}%`;
    this.phase.textContent = `${p.phase.toUpperCase()} · ${String(Math.round(f * 100))}%`;
  }

  phaseText(text: string): void {
    this.phase.textContent = text;
  }

  addNote(msg: string): void {
    let note = this.el.querySelector<HTMLDivElement>('.note');
    if (!note) {
      note = document.createElement('div');
      note.className = 'note';
      this.el.querySelector('.card')?.appendChild(note);
    }
    note.textContent = msg;
  }

  error(msg: string, onRetry?: () => void): void {
    this.el.classList.add('error');
    this.phase.textContent = msg;
    if (onRetry) {
      let btn = this.el.querySelector<HTMLButtonElement>('.retry-btn');
      if (!btn) {
        btn = document.createElement('button');
        btn.className = 'retry-btn';
        btn.textContent = 'Retry';
        this.el.querySelector('.card')?.appendChild(btn);
      }
      btn.onclick = () => {
        btn.remove();
        this.el.classList.remove('error');
        this.phase.textContent = 'RETRYING...';
        onRetry();
      };
    }
  }

  /** Fade out and remove; resolves once the overlay is gone (it covers the stage until then). */
  hide(): Promise<void> {
    this.el.classList.add('done');
    return new Promise((resolve) => {
      setTimeout(() => {
        this.el.remove();
        resolve();
      }, 400);
    });
  }
}

const POSITIONS_FASTF1 = 'Positions are map-matched from F1 livetiming.';
const POSITIONS_RECON = 'Positions are reconstructed from lap-by-lap timing, not GPS: every car is placed on the track between its timing lines.';
const MODELLED = [
  'Car positions between the timing lines',
  'Where a pass happens (the order change is timed, the place is not)',
  'Speed (shown with ~)',
  'Pit-lane movement and stationary time',
  'Safety-car spacing',
  'Engine sound (derived from the modelled speed)',
];

/** The map and tower legend (the landscape legend panel's entries; portrait has no legend panel). */
export const LEGEND = [
  'Slipstream bond: a car less than 1.0 s behind the car ahead (thicker and brighter = closer; beads flow)',
  'Flashing cyan: DRS open (a reconstructed race shows DRS zones as cyan dots, approximate)',
  'Bracket: a train, 3 or more cars linked',
  '+1 / -1: a pass, popped where it happened (the place is modelled in a reconstructed race)',
  'Pip: a pass site; pips stack up',
  'Highlighted tower rows: the cars in the battle cam',
  'Purple: the fastest lap holder',
  'T-cam: yellow = the higher car number',
] as const;

/** the help overlay's reconstruction block: what is modelled, the timing source, and the facts hosts when the bundle names them */
export function reconHelpHtml(factHosts: readonly string[]): string {
  return `<div class="subtitle">WHAT IS MODELLED</div><ul>${MODELLED.map((m) => `<li>${esc(m)}</li>`).join('')}</ul>`
    + '<div>Timing: Jolpica-F1 (Ergast)</div>'
    + (factHosts.length > 0 ? `<div>Facts: ${esc(factHosts.join(', '))}</div>` : '');
}

const LITE_OPTIONS = [['auto', 'AUTO'], ['on', 'ON'], ['off', 'OFF']] as const;
/** the help labels of the LITE rungs, in order */
export const LITE_RUNG_LABELS = ['30 FPS', 'LOWER MAP RESOLUTION', 'SOFTER BATTLE CAM'] as const;

export class HelpOverlay {
  readonly el: HTMLDivElement;

  constructor(parent: HTMLElement, attribution: () => string, onLite?: (s: LiteSetting) => void) {
    this.el = document.createElement('div');
    this.el.id = 'help';
    this.el.hidden = true;
    const rows = KEY_HELP.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('');
    this.el.innerHTML = `
      <div class="card" role="dialog" aria-label="Help">
        <div class="title">UNDERCUT · KEYS</div>
        <table>${rows}</table>
        <div class="legend"><div class="subtitle">LEGEND</div><ul>${LEGEND.map((l) => `<li>${esc(l)}</li>`).join('')}</ul></div>
        <div class="lite"><div class="subtitle">LITE · SMOOTHER PLAYBACK</div>
          <div class="lite-pick" role="group" aria-label="LITE">${LITE_OPTIONS.map(([v, l]) => `<button type="button" data-lite="${v}">${l}</button>`).join('')}</div>
          <div class="lite-rungs"></div></div>
        <div class="fs-tip" hidden>On iPhone: Share → Add to Home Screen for full screen.</div>
        <div class="audio" hidden><div class="subtitle">SOUND</div><ul class="audio-labels"></ul></div>
        <div class="note">Each race bundle carries its own fitted preset (map rotation and scale
        differ per circuit). <span class="positions">${esc(POSITIONS_FASTF1)}</span>
        Map dots and car sprites are not to scale.</div>
        <div class="recon" hidden></div>
        <div class="attr"></div>
        <div class="version">Undercut web player ${esc(VERSION_LABEL)}</div>
      </div>`;
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.hide();
    });
    parent.appendChild(this.el);
    this.el.querySelector('.lite-pick')?.addEventListener('click', (e) => {
      const v = (e.target as HTMLElement).closest<HTMLElement>('[data-lite]')?.dataset.lite;
      if (v === 'auto' || v === 'on' || v === 'off') onLite?.(v);
    });
    // P-2: no Fullscreen API for documents (iPhone Safari) → no ⛶ button; say how to go full
    // screen there instead
    if (!document.fullscreenEnabled) (this.el.querySelector('.fs-tip') as HTMLDivElement).hidden = false;
    const attr = this.el.querySelector('.attr') as HTMLDivElement;
    this.refreshAttr = (): void => {
      attr.textContent = attribution();
    };
  }

  /**
   * Reconstructed races: replace the positions sentence with the reconstruction text and list what is
   * modelled and where the facts come from (`factHosts`, e.g. the commentary race_sources hostnames).
   * Third-party-dataset races (positions: 'tracinginsights'): the TI sentence, no modelled-fields list.
   * `null` restores the FastF1 text.
   */
  setReconstruction(prov: Provenance | null, factHosts: readonly string[] = []): void {
    const pos = this.el.querySelector('.positions') as HTMLSpanElement;
    const box = this.el.querySelector('.recon') as HTMLDivElement;
    // the pure decision in core/provenance.ts (impl-gate F1: call sites pass provenanceOf(man)
    // unconditionally -- a TI race is 'ti', never a null that falls back to the FastF1 sentence)
    switch (positionsHelpKind(prov)) {
      case 'recon':
        pos.textContent = POSITIONS_RECON;
        box.hidden = false;
        box.innerHTML = reconHelpHtml(factHosts);
        break;
      case 'ti':
        pos.textContent = TI_POSITIONS_LABEL;
        box.hidden = true;
        box.innerHTML = '';
        break;
      default:
        pos.textContent = POSITIONS_FASTF1;
        box.hidden = true;
        box.innerHTML = '';
    }
  }

  /** The LITE setting (pressed button) and the active rungs, by their help labels. */
  setLite(setting: LiteSetting, rung: number): void {
    for (const b of this.el.querySelectorAll<HTMLButtonElement>('[data-lite]')) b.setAttribute('aria-pressed', String(b.dataset.lite === setting));
    (this.el.querySelector('.lite-rungs') as HTMLDivElement).textContent = rung > 0 ? `ACTIVE: ${LITE_RUNG_LABELS.slice(0, rung).join(' · ')}` : 'NOT ACTIVE';
  }

  private readonly refreshAttr: () => void;
  /** The honesty labels of the sound layers this race has (see `audioLabels`). */
  setAudioLabels(labels: readonly string[]): void {
    const box = this.el.querySelector('.audio') as HTMLDivElement;
    (box.querySelector('.audio-labels') as HTMLUListElement).innerHTML = labels.map((l) => `<li>${esc(l)}</li>`).join('');
    box.hidden = labels.length === 0;
  }

  get open(): boolean {
    return !this.el.hidden;
  }

  toggle(): void {
    if (this.open) this.hide();
    else this.show();
  }

  show(): void {
    this.refreshAttr();
    this.el.hidden = false;
  }

  hide(): void {
    this.el.hidden = true;
  }
}

/** The small label strip shown when sound is first enabled; it dismisses itself. */
export class AudioNotice {
  readonly el: HTMLDivElement;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(parent: HTMLElement, private readonly ms = 8000) {
    this.el = document.createElement('div');
    this.el.id = 'audio-note';
    this.el.hidden = true;
    this.el.setAttribute('role', 'status');
    parent.appendChild(this.el);
  }

  show(labels: readonly string[]): void {
    this.el.innerHTML = labels.map((l) => `<div>${esc(l)}</div>`).join('');
    this.el.hidden = false;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.el.hidden = true;
    }, this.ms);
  }
}

/** The automatic LITE switch-on notice: `LITE ON · SMOOTHER PLAYBACK` for 3 s with an UNDO tap target (>= 44 CSS px). */
export class LiteNotice {
  readonly el: HTMLDivElement;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(parent: HTMLElement, onUndo: () => void, private readonly ms = 3000) {
    this.el = document.createElement('div');
    this.el.id = 'lite-note';
    this.el.hidden = true;
    this.el.setAttribute('role', 'status');
    this.el.innerHTML = '<span>LITE ON · SMOOTHER PLAYBACK</span><button type="button">UNDO</button>';
    this.el.querySelector('button')?.addEventListener('click', () => {
      this.hide();
      onUndo();
    });
    parent.appendChild(this.el);
  }

  show(): void {
    this.el.hidden = false;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.hide(); }, this.ms);
  }

  hide(): void {
    clearTimeout(this.timer);
    this.el.hidden = true;
  }
}

/** Why the gate is up (plan §3.6; portrait plan P2: a phone held sideways asks to rotate). */
export const GATE_TEXT = { rotate: 'ROTATE TO PORTRAIT', small: 'WINDOW TOO SMALL' } as const;

/**
 * The one overlay for windows the UI does not support: a touch landscape window with a short side
 * under 500 CSS px (rotate), or too small (`resolveScale`). It covers the stage while the player is
 * paused and never resumes it; the player hides it once a resize brings the window back in range.
 * Text is tier L (`--font-l`).
 */
export class LandscapeGate {
  readonly el: HTMLDivElement;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.id = 'gate';
    this.el.setAttribute('role', 'alert');
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  get visible(): boolean {
    return !this.el.hidden;
  }

  show(why: keyof typeof GATE_TEXT): void {
    this.el.textContent = GATE_TEXT[why];
    this.el.dataset.why = why;
    this.el.hidden = false;
  }

  hide(): void {
    this.el.hidden = true;
  }
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Frame-time statistics over a sliding window (wall ms). */
export class FrameStats {
  private readonly buf: Float64Array;
  private n = 0;
  private i = 0;
  frames = 0;

  constructor(size = 240) {
    this.buf = new Float64Array(size);
  }

  push(ms: number): void {
    this.buf[this.i] = ms;
    this.i = (this.i + 1) % this.buf.length;
    this.n = Math.min(this.n + 1, this.buf.length);
    this.frames++;
  }

  get count(): number {
    return this.n;
  }

  mean(): number {
    let s = 0;
    for (let k = 0; k < this.n; k++) s += this.buf[k] ?? 0;
    return this.n ? s / this.n : 0;
  }

  percentile(p: number): number {
    if (!this.n) return 0;
    const a = Array.from(this.buf.subarray(0, this.n)).sort((x, y) => x - y);
    return a[Math.min(a.length - 1, Math.floor(p * a.length))] ?? 0;
  }

  reset(): void {
    this.n = 0;
    this.i = 0;
  }
}

export class DebugOverlay {
  readonly el: HTMLPreElement;
  private nextUpdate = 0;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('pre');
    this.el.id = 'debug';
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  get open(): boolean {
    return !this.el.hidden;
  }

  toggle(): void {
    this.el.hidden = !this.el.hidden;
    this.nextUpdate = 0;
  }

  /** Update at 4 Hz while open; `text` is only built when needed. */
  update(nowMs: number, text: () => string): void {
    if (this.el.hidden || nowMs < this.nextUpdate) return;
    this.nextUpdate = nowMs + 250;
    this.el.textContent = text();
  }
}
